// Simple color extraction and theming based on a logo image
// Picks two dominant-ish colors by quantizing pixels and choosing top buckets.

function quantizeColor(r, g, b, step) {
  const q = (v) => Math.min(255, Math.max(0, Math.round(v / step) * step));
  return `${q(r)},${q(g)},${q(b)}`;
}

function rgbStringToHex(rgb) {
  const [r, g, b] = rgb.split(',').map((v) => parseInt(v, 10));
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function lightenOrDarkenColor(hex, amount) {
  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00ff) + amount;
  let b = (num & 0x0000ff) + amount;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function toRgba(hex, alpha) {
  const num = parseInt(hex.slice(1), 16);
  const r = num >> 16;
  const g = (num >> 8) & 0x00ff;
  const b = num & 0x0000ff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export async function initThemeFromLogo(logoPath = '/logo.png') {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const loaded = new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });
    img.src = logoPath;
    await loaded;

    const width = 80;
    const height = Math.max(1, Math.round((img.height / img.width) * width));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    const { data } = ctx.getImageData(0, 0, width, height);

    const buckets = new Map();
    const step = 24; // quantization step per channel
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3];
      if (a < 200) continue; // ignore transparent/very light pixels
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      // Avoid near-white/near-black extremes
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      if (max > 245 || min < 15) continue;
      const key = quantizeColor(r, g, b, step);
      buckets.set(key, (buckets.get(key) || 0) + 1);
    }

    const sorted = [...buckets.entries()].sort((a, b) => b[1] - a[1]);
    // Pick top two distinct colors (avoid too similar)
    let primary = sorted[0] ? rgbStringToHex(sorted[0][0]) : '#3b82f6';
    let secondary = '#8b5cf6';
    for (let i = 1; i < sorted.length; i += 1) {
      const candidate = rgbStringToHex(sorted[i][0]);
      // simple distance check in RGB space
      const d = colorDistance(primary, candidate);
      if (d > 80) {
        secondary = candidate;
        break;
      }
    }

    // Derive shades for primary
    const p400 = lightenOrDarkenColor(primary, 10);
    const p500 = primary;
    const p600 = lightenOrDarkenColor(primary, -15);
    const p700 = lightenOrDarkenColor(primary, -30);

    const root = document.documentElement;
    root.style.setProperty('--primary-400', p400);
    root.style.setProperty('--primary-400-10', toRgba(p400, 0.1));
    root.style.setProperty('--primary-400-05', toRgba(p400, 0.05));
    root.style.setProperty('--primary-400-20', toRgba(p400, 0.2));
    root.style.setProperty('--primary-500', p500);
    root.style.setProperty('--primary-500-20', toRgba(p500, 0.2));
    root.style.setProperty('--primary-600', p600);
    root.style.setProperty('--primary-700', p700);
    root.style.setProperty('--secondary-500', secondary);
  } catch (e) {
    // Fallback silently
    // eslint-disable-next-line no-console
    console.warn('Theme init failed, using defaults', e);
  }
}

function colorDistance(hex1, hex2) {
  const n1 = parseInt(hex1.slice(1), 16);
  const n2 = parseInt(hex2.slice(1), 16);
  const r1 = n1 >> 16;
  const g1 = (n1 >> 8) & 0xff;
  const b1 = n1 & 0xff;
  const r2 = n2 >> 16;
  const g2 = (n2 >> 8) & 0xff;
  const b2 = n2 & 0xff;
  const dr = r1 - r2;
  const dg = g1 - g2;
  const db = b1 - b2;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}


